import type React from 'react'
import { useEffect, useState } from 'react'

import { EMAIL_REGEX } from '@/constants/constants'

type Rule = {
  required?: boolean
  minLength?: number
  maxLength?: number
  email?: string | boolean
}

type FormFields = {
  emailAddress: string
  password: string
  coupon: string
  firstName: string
  lastName: string
  companyName: string
  country: string
}

const useForm = () => {
  const [form, setForm] = useState<Partial<FormFields>>({})
  const [rules, setRules] = useState<Record<string, Rule>>({})
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isRequested, setIsRequested] = useState(false)

  useEffect(() => {
    if (isRequested) {
      setErrors(validate())
    }
  }, [form])

  const onSubmit = (
    e: React.ChangeEvent<HTMLFormElement>,
    cb: (e: React.ChangeEvent<HTMLFormElement>) => void
  ) => {
    e.preventDefault()

    const errors = validate()

    setIsRequested(true)

    if (Object.keys(errors).length) {
      return setErrors(errors)
    }

    return cb(e)
  }

  const validate = () => {
    const errors: {
      [key: string]: string
    } = {}

    Object.keys(rules).forEach((key) => {
      const value = form[key as keyof typeof form]

      if (rules[key] && typeof value === 'string') {
        Object.keys(rules[key]).forEach(() => {
          const rule = rules[key]

          if (rule.minLength && value.length < rule.minLength) {
            errors[key] =
              `This field must be at least ${rule.minLength} characters long`
          }

          if (rule.maxLength && value.length > rule.maxLength) {
            errors[key] =
              `This field must be at most ${rule.maxLength} characters long`
          }

          if (rule.email && !EMAIL_REGEX.test(value)) {
            errors[key] = 'This field must be a valid email address'
          }

          if (rule.required && !value) {
            errors[key] = 'This field is required'
          }
        })
      }
    })

    return errors
  }

  const setRule = (key: string, rule: Rule) => {
    setRules((prevState) => ({
      ...prevState,
      [key]: rule,
    }))
  }

  const register = (
    name: string,
    defaultValue = '',
    rules: Rule | null = null,
    cb: (e: string) => any = () => {}
  ) => {
    useEffect(() => {
      setForm((prevForm) => ({
        ...prevForm,
        [name]: defaultValue,
      }))

      if (rules) {
        setRule(name, rules)
      }
    }, [defaultValue])

    return {
      defaultValue,
      onChange(e: React.ChangeEvent<HTMLInputElement> | string) {
        const nameValue = typeof e === 'string' ? e : e.target.value
        setForm({
          ...form,
          [name]: nameValue,
        })

        cb(nameValue)
      },
    }
  }

  return {
    onSubmit,
    register,
    errors,
    setErrors,
    form,
    setForm,
    setRule,
  }
}

export default useForm
